import { HardHat } from 'lucide-react';
import { formatRange } from './utils';
import { formatMinor, TaxEmptyState, TaxReportCard, TaxStatCell, TaxStatStrip } from './taxReportShared';

interface Subcontractor {
  subcontractorId: string;
  legalName: string;
  utr: string | null;
  totalPaymentsMinor: number;
  labourMinor: number;
  materialsMinor: number;
  deductionMinor: number;
  deductionRateBps: number;
}

interface Data {
  type: string;
  periodStart: string;
  periodEnd: string;
  subcontractors: Subcontractor[];
  totalPaymentsMinor: number;
  totalDeductionsMinor: number;
}

export function TaxCis300Report({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const subs = d.subcontractors ?? [];

  return (
    <TaxReportCard
      icon={HardHat}
      title="CIS300 — Monthly Return"
      badge={formatRange(d.periodStart, d.periodEnd)}
    >
      <TaxStatStrip>
        <TaxStatCell label="Subcontractors" value={subs.length} />
        <TaxStatCell label="Total paid" value={formatMinor(d.totalPaymentsMinor, 'GBP')} />
        <TaxStatCell label="Deductions" value={formatMinor(d.totalDeductionsMinor, 'GBP')} emphasis />
      </TaxStatStrip>

      {subs.length === 0 ? (
        <TaxEmptyState message="No subcontractor payments recorded for this period." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-50/60 text-[11px] uppercase tracking-[0.06em] text-surface-500">
                <th className="px-4.5 py-2 text-left font-medium">Subcontractor</th>
                <th className="px-3 py-2 text-left font-medium">UTR</th>
                <th className="px-3 py-2 text-right font-medium">Labour</th>
                <th className="px-3 py-2 text-right font-medium">Materials</th>
                <th className="px-3 py-2 text-right font-medium">Rate</th>
                <th className="px-4.5 py-2 text-right font-medium">Deduction</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr
                  key={s.subcontractorId}
                  className="border-t border-surface-100 transition-colors hover:bg-surface-50"
                >
                  <td className="px-4.5 py-1.75 text-[13px] text-surface-700">{s.legalName}</td>
                  <td className="px-3 py-1.75 font-mono text-[12px] tabular-nums text-surface-500">
                    {s.utr ?? '—'}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[13px] tabular-nums text-surface-600">
                    {formatMinor(s.labourMinor, 'GBP')}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[13px] tabular-nums text-surface-600">
                    {formatMinor(s.materialsMinor, 'GBP')}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[12px] tabular-nums text-surface-500">
                    {(s.deductionRateBps / 100).toFixed(0)}%
                  </td>
                  <td className="px-4.5 py-1.75 text-right font-mono text-[13px] font-semibold tabular-nums text-surface-900">
                    {formatMinor(s.deductionMinor, 'GBP')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TaxReportCard>
  );
}
